import { Badge, Button, Card, CardBody, CardHeader, FormField, Modal, Spinner, Surface } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PickTask } from '@warehouse/shared';
import { ArrowLeft, Boxes, Printer } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

import { PageHeader } from '../../../components/PageHeader';
import { formatDate, formatQty } from '../../../lib/format';
import { toastError, toastInfo, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import { pickingKeys, roundsApi, tasksApi } from '../api';
import { PickListPrintView } from '../components/PickListPrintView';
import { ModeBadge, RoundStatusBadge, TaskStatusBadge } from '../components/StatusBadge';

/**
 * One pick round: its tasks in flow order, the staging plan, the printable list
 * and — for paper rounds — the confirmation form that applies the actual
 * quantities through the same commands the scanner uses.
 */
export function PickRoundDetailPage(): ReactNode {
  const { t } = useTranslation('pickingShipping');
  const { t: tc } = useTranslation('common');
  const { roundId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [printing, setPrinting] = useState(false);
  const [staging, setStaging] = useState(false);
  const [confirmingPaper, setConfirmingPaper] = useState(false);
  const [confirmTask, setConfirmTask] = useState<PickTask | null>(null);
  const [shortTask, setShortTask] = useState<PickTask | null>(null);

  const taskListState = useMemo(
    () => ({ filters: { roundId }, sort: 'sortKey', dir: 'asc' as const, pageSize: 500 }),
    [roundId],
  );

  const round = useQuery({
    queryKey: pickingKeys.round(roundId),
    queryFn: () => roundsApi.get(roundId),
    enabled: roundId.length > 0,
  });
  const tasks = useQuery({
    queryKey: pickingKeys.tasks(taskListState),
    queryFn: () => tasksApi.list(taskListState),
    enabled: roundId.length > 0,
  });

  useLiveQuery(['pick-rounds', 'pick-tasks'], ['picking'], { exact: false });

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['picking'] });
  };

  const start = useMutation({
    mutationFn: () => roundsApi.start(roundId),
    onSuccess: async () => {
      toastSuccess(t('rounds.started'));
      await invalidate();
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  const close = useMutation({
    mutationFn: () => roundsApi.close(roundId),
    onSuccess: async () => {
      toastSuccess(t('rounds.closed'));
      await invalidate();
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  const claim = useMutation({
    mutationFn: (taskIds: string[]) => tasksApi.claim({ taskIds }),
    onSuccess: async () => {
      toastSuccess(t('tasks.claimedToast'));
      await invalidate();
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  const print = useMutation({
    mutationFn: () => roundsApi.print(roundId),
    onSuccess: (result) => {
      if (result.routed) toastSuccess(t('paper.printQueued'));
      else toastInfo(t('paper.printNotRouted'));
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  if (round.isLoading) return <Spinner />;
  if (!round.data) return null;

  const data = round.data;
  const rows = tasks.data?.items ?? [];

  return (
    <>
      <PageHeader
        breadcrumb={
          <Link to="/orders/picking" className="inline-flex items-center gap-1 hover:text-ink">
            <ArrowLeft className="size-3" aria-hidden /> {t('rounds.title')}
          </Link>
        }
        title={data.roundNo}
        subtitle={`${formatDate(data.deliveryDate)} · ${data.orders.map((o) => o.restaurantName).join(', ')}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <RoundStatusBadge status={data.status} />
            <ModeBadge mode={data.mode} />
            <Button
              variant="secondary"
              size="sm"
              icon={<Boxes className="size-4" />}
              onClick={() => {
                setStaging(true);
              }}
            >
              {t('staging.title')}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              icon={<Printer className="size-4" />}
              onClick={() => {
                setPrinting(true);
              }}
            >
              {t('paper.pickList')}
            </Button>
            {data.status === 'planned' ? (
              <Button
                size="sm"
                loading={start.isPending}
                onClick={() => {
                  start.mutate();
                }}
              >
                {t('rounds.start')}
              </Button>
            ) : null}
            {data.mode === 'paper' && data.status !== 'closed' ? (
              <Button
                size="sm"
                onClick={() => {
                  setConfirmingPaper(true);
                }}
              >
                {t('paper.title')}
              </Button>
            ) : null}
            {data.status === 'picked' || data.status === 'packed' ? (
              <Button
                variant="ghost"
                size="sm"
                loading={close.isPending}
                onClick={() => {
                  close.mutate();
                }}
              >
                {t('rounds.close')}
              </Button>
            ) : null}
          </div>
        }
      />

      <Surface className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-border text-left text-xs uppercase tracking-wider text-ink-muted">
                <th className="px-4 py-2">{t('tasks.sortKey')}</th>
                <th className="px-4 py-2">{t('tasks.bin')}</th>
                <th className="px-4 py-2">{tc('common.article')}</th>
                <th className="px-4 py-2">{t('tasks.lot')}</th>
                <th className="px-4 py-2 text-right">{t('tasks.qty')}</th>
                <th className="px-4 py-2 text-right">{t('tasks.picked')}</th>
                <th className="px-4 py-2">{tc('common.status')}</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border">
              {rows.map((task) => (
                <tr key={task.id} data-testid={`task-row-${task.sortKey}`}>
                  <td className="px-4 py-2 font-mono text-xs text-ink-muted">{task.sortKey + 1}</td>
                  <td className="px-4 py-2 font-mono">
                    {task.actualBinCode ?? task.suggestedBinCode ?? '—'}
                    {task.actualBinCode && task.actualBinCode !== task.suggestedBinCode ? (
                      <Badge tone="warning" size="sm" className="ml-2">
                        {t('tasks.actual')}
                      </Badge>
                    ) : null}
                  </td>
                  <td className="px-4 py-2">
                    <span className="font-mono text-xs text-ink-muted">{task.articleNo}</span>{' '}
                    {task.articleName}
                    <span className="block text-xs text-ink-muted">{task.orderNo}</span>
                  </td>
                  <td className="px-4 py-2">
                    {task.actualLotNo ?? task.suggestedLotNo ?? '—'}
                    {task.suggestedBestBefore ? (
                      <span className="block text-xs text-ink-muted">{task.suggestedBestBefore}</span>
                    ) : null}
                  </td>
                  <td className="px-4 py-2 text-right font-mono">{formatQty(task.qtyBase)}</td>
                  <td className="px-4 py-2 text-right font-mono">{formatQty(task.pickedQtyBase)}</td>
                  <td className="px-4 py-2">
                    <TaskStatusBadge status={task.status} />
                    {task.shortageReason ? (
                      <span className="block text-xs text-ink-muted">{task.shortageReason}</span>
                    ) : null}
                  </td>
                  <td className="px-4 py-2">
                    {task.status === 'open' || task.status === 'claimed' ? (
                      <div className="flex justify-end gap-1">
                        {task.status === 'open' ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              claim.mutate([task.id]);
                            }}
                          >
                            {t('tasks.claim')}
                          </Button>
                        ) : null}
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setConfirmTask(task);
                          }}
                        >
                          {t('tasks.confirm')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setShortTask(task);
                          }}
                        >
                          {t('tasks.markShort')}
                        </Button>
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && !tasks.isLoading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-ink-muted">
                    {t('tasks.empty')}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </Surface>

      {confirmTask ? (
        <ConfirmPickModal
          task={confirmTask}
          onClose={() => {
            setConfirmTask(null);
          }}
          onDone={invalidate}
        />
      ) : null}

      {shortTask ? (
        <ShortageModal
          task={shortTask}
          onClose={() => {
            setShortTask(null);
          }}
          onDone={invalidate}
        />
      ) : null}

      {staging ? (
        <StagingModal
          roundId={roundId}
          onClose={() => {
            setStaging(false);
          }}
          onDone={invalidate}
        />
      ) : null}

      {printing ? (
        <PrintModal
          roundId={roundId}
          onClose={() => {
            setPrinting(false);
          }}
          onPrintServer={() => {
            print.mutate();
          }}
          printing={print.isPending}
        />
      ) : null}

      {confirmingPaper ? (
        <PaperConfirmModal
          roundId={roundId}
          tasks={rows}
          onClose={() => {
            setConfirmingPaper(false);
          }}
          onDone={async () => {
            setConfirmingPaper(false);
            await invalidate();
          }}
        />
      ) : null}

      {round.data.status === 'closed' ? null : (
        <p className="mt-4 text-xs text-ink-muted">
          {tc('common.updatedAt')}: {formatDate(data.updatedAt)}
        </p>
      )}

      <button type="button" className="sr-only" onClick={() => void navigate('/orders/picking')}>
        {tc('actions.back')}
      </button>
    </>
  );
}

// ---------------------------------------------------------------------------

function ConfirmPickModal({
  task,
  onClose,
  onDone,
}: {
  task: PickTask;
  onClose: () => void;
  onDone: () => Promise<void>;
}): ReactNode {
  const { t } = useTranslation('pickingShipping');
  const { t: tc } = useTranslation('common');
  const [binCode, setBinCode] = useState(task.suggestedBinCode ?? '');
  const [palletCode, setPalletCode] = useState(task.suggestedLpn ?? '');
  const [qty, setQty] = useState(
    String(Math.max(Number(task.qtyBase) - Number(task.pickedQtyBase), 0)),
  );

  const differs = binCode.trim().toUpperCase() !== (task.suggestedBinCode ?? '').toUpperCase();

  const confirm = useMutation({
    mutationFn: () =>
      tasksApi.confirm(task.id, {
        binCode: binCode.trim() || null,
        palletCode: palletCode.trim() || null,
        qtyBase: qty,
      }),
    onSuccess: async () => {
      toastSuccess(t('tasks.confirmedToast'));
      await onDone();
      onClose();
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  return (
    <Modal open onClose={onClose} title={t('tasks.confirmDialog.title')} subtitle={task.articleName} width={480}>
      <div className="flex flex-col gap-4">
        <FormField
          label={t('tasks.confirmDialog.scanBin')}
          value={binCode}
          autoFocus
          onChange={(event) => {
            setBinCode(event.target.value);
          }}
        />
        <FormField
          label={t('tasks.confirmDialog.scanPallet')}
          value={palletCode}
          onChange={(event) => {
            setPalletCode(event.target.value);
          }}
        />
        <FormField
          label={t('tasks.confirmDialog.quantity')}
          inputMode="decimal"
          value={qty}
          onChange={(event) => {
            setQty(event.target.value);
          }}
        />
        {differs ? (
          <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
            {t('tasks.confirmDialog.differsFromSuggestion')}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            {tc('actions.cancel')}
          </Button>
          <Button
            loading={confirm.isPending}
            onClick={() => {
              confirm.mutate();
            }}
          >
            {t('tasks.confirmDialog.submit')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ShortageModal({
  task,
  onClose,
  onDone,
}: {
  task: PickTask;
  onClose: () => void;
  onDone: () => Promise<void>;
}): ReactNode {
  const { t } = useTranslation('pickingShipping');
  const { t: tc } = useTranslation('common');
  const [qty, setQty] = useState('0');
  const [reason, setReason] = useState('');

  const short = useMutation({
    mutationFn: () =>
      tasksApi.short(task.id, {
        pickedQtyBase: qty,
        reason,
        binCode: task.suggestedBinCode,
      }),
    onSuccess: async () => {
      toastSuccess(t('tasks.shortToast'));
      await onDone();
      onClose();
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  return (
    <Modal open onClose={onClose} title={t('tasks.shortDialog.title')} subtitle={task.articleName} width={440}>
      <div className="flex flex-col gap-4">
        <FormField
          label={t('tasks.shortDialog.pickedQty')}
          inputMode="decimal"
          value={qty}
          onChange={(event) => {
            setQty(event.target.value);
          }}
        />
        <FormField
          label={t('tasks.shortDialog.reason')}
          placeholder={t('tasks.shortDialog.reasonPlaceholder')}
          value={reason}
          autoFocus
          onChange={(event) => {
            setReason(event.target.value);
          }}
        />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            {tc('actions.cancel')}
          </Button>
          <Button
            variant="danger"
            loading={short.isPending}
            disabled={reason.trim().length === 0}
            onClick={() => {
              short.mutate();
            }}
          >
            {t('tasks.shortDialog.submit')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function StagingModal({
  roundId,
  onClose,
  onDone,
}: {
  roundId: string;
  onClose: () => void;
  onDone: () => Promise<void>;
}): ReactNode {
  const { t } = useTranslation('pickingShipping');
  const { t: tc } = useTranslation('common');
  const [palletCode, setPalletCode] = useState('');

  const plan = useQuery({
    queryKey: pickingKeys.staging(roundId),
    queryFn: () => roundsApi.staging(roundId),
  });

  const stage = useMutation({
    mutationFn: () => roundsApi.stage(roundId, { palletCode }),
    onSuccess: async () => {
      toastSuccess(t('staging.stagedToast', { bin: plan.data?.stagingBinCode ?? '' }));
      setPalletCode('');
      await plan.refetch();
      await onDone();
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  return (
    <Modal open onClose={onClose} title={t('staging.title')} subtitle={t('staging.subtitle')} width={720}>
      <div className="flex flex-col gap-4">
        {plan.isLoading ? <Spinner /> : null}

        {plan.data && !plan.data.stagingBinCode ? (
          <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">{t('staging.noStagingBin')}</p>
        ) : null}

        {plan.data?.stagingBinCode ? (
          <p className="text-sm text-ink-muted">
            {t('staging.stagingBin')}: <span className="font-mono">{plan.data.stagingBinCode}</span>
          </p>
        ) : null}

        <div className="flex items-end gap-2">
          <FormField
            label={t('staging.scanPallet')}
            className="flex-1"
            value={palletCode}
            onChange={(event) => {
              setPalletCode(event.target.value);
            }}
          />
          <Button
            loading={stage.isPending}
            disabled={palletCode.trim().length === 0}
            onClick={() => {
              stage.mutate();
            }}
          >
            {t('staging.stagePallet')}
          </Button>
        </div>

        {plan.data && plan.data.items.length === 0 ? (
          <p className="text-sm text-ink-muted">{t('staging.allStaged')}</p>
        ) : null}

        <ul className="flex max-h-80 flex-col gap-2 overflow-y-auto">
          {plan.data?.items.map((item) => (
            <li key={item.articleId} className="rounded-lg border border-surface-border p-3">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-mono text-xs text-ink-muted">{item.articleNo}</span>
                <span className="font-medium">{item.articleName}</span>
                <span className="ml-auto font-mono text-xs">
                  {t('staging.required')} {formatQty(item.requiredBase)} · {t('staging.staged')}{' '}
                  {formatQty(item.stagedBase)}
                </span>
              </div>
              <ul className="mt-2 flex flex-wrap gap-1.5">
                {item.lines.map((line, index) => (
                  <li key={`${line.binId}-${line.lotId ?? index}`}>
                    <Badge tone="neutral" size="sm">
                      {line.binCode} · {formatQty(line.qtyBase)}
                      {line.lotNo ? ` · ${line.lotNo}` : ''}
                    </Badge>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>

        <div className="flex justify-end">
          <Button variant="ghost" onClick={onClose}>
            {tc('actions.close')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function PrintModal({
  roundId,
  onClose,
  onPrintServer,
  printing,
}: {
  roundId: string;
  onClose: () => void;
  onPrintServer: () => void;
  printing: boolean;
}): ReactNode {
  const { t } = useTranslation('pickingShipping');
  const { t: tc } = useTranslation('common');
  const document = useQuery({
    queryKey: pickingKeys.pickList(roundId),
    queryFn: () => roundsApi.pickList(roundId),
  });

  return (
    <Modal open onClose={onClose} title={t('paper.pickList')} width={900}>
      <div className="flex flex-col gap-4">
        {document.isLoading ? <Spinner /> : null}
        {document.data ? <PickListPrintView document={document.data} /> : null}
        <div className="flex justify-end gap-2 print:hidden">
          <Button variant="ghost" onClick={onClose}>
            {tc('actions.close')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              globalThis.print();
            }}
          >
            {tc('actions.print')}
          </Button>
          <Button loading={printing} onClick={onPrintServer}>
            {t('paper.print')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function PaperConfirmModal({
  roundId,
  tasks,
  onClose,
  onDone,
}: {
  roundId: string;
  tasks: PickTask[];
  onClose: () => void;
  onDone: () => Promise<void>;
}): ReactNode {
  const { t } = useTranslation('pickingShipping');
  const { t: tc } = useTranslation('common');
  const open = tasks.filter((task) => task.status === 'open' || task.status === 'claimed');

  const [values, setValues] = useState<Record<string, { qty: string; reason: string }>>(() =>
    Object.fromEntries(
      open.map((task) => [
        task.id,
        { qty: String(Math.max(Number(task.qtyBase) - Number(task.pickedQtyBase), 0)), reason: '' },
      ]),
    ),
  );

  const confirm = useMutation({
    mutationFn: () =>
      roundsApi.confirmRound(roundId, {
        lines: open.map((task) => ({
          taskId: task.id,
          pickedQtyBase: values[task.id]?.qty ?? '0',
          ...(values[task.id]?.reason.trim()
            ? { shortageReason: values[task.id]!.reason.trim() }
            : {}),
        })),
      }),
    onSuccess: async (result) => {
      toastSuccess(t('paper.confirmed', { confirmed: result.confirmed, short: result.short }));
      await onDone();
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  return (
    <Modal open onClose={onClose} title={t('paper.title')} subtitle={t('paper.subtitle')} width={820}>
      <div className="flex flex-col gap-4">
        {open.length === 0 ? (
          <p className="text-sm text-ink-muted">{t('paper.nothingToConfirm')}</p>
        ) : (
          <Card>
            <CardHeader>
              <span className="text-sm font-semibold">{t('tasks.title')}</span>
            </CardHeader>
            <CardBody className="max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-ink-muted">
                    <th className="py-1">{t('tasks.sortKey')}</th>
                    <th className="py-1">{tc('common.article')}</th>
                    <th className="py-1 text-right">{t('tasks.qty')}</th>
                    <th className="py-1">{t('paper.pickedQty')}</th>
                    <th className="py-1">{t('paper.shortageReason')}</th>
                  </tr>
                </thead>
                <tbody>
                  {open.map((task) => (
                    <tr key={task.id}>
                      <td className="py-1 font-mono text-xs">{task.sortKey + 1}</td>
                      <td className="py-1">{task.articleName}</td>
                      <td className="py-1 text-right font-mono">{formatQty(task.qtyBase)}</td>
                      <td className="py-1">
                        <input
                          aria-label={`${t('paper.pickedQty')} ${task.articleName}`}
                          className="w-24 rounded border border-surface-border px-2 py-1"
                          inputMode="decimal"
                          value={values[task.id]?.qty ?? ''}
                          onChange={(event) => {
                            setValues((prev) => ({
                              ...prev,
                              [task.id]: { qty: event.target.value, reason: prev[task.id]?.reason ?? '' },
                            }));
                          }}
                        />
                      </td>
                      <td className="py-1">
                        <input
                          aria-label={`${t('paper.shortageReason')} ${task.articleName}`}
                          className="w-40 rounded border border-surface-border px-2 py-1"
                          value={values[task.id]?.reason ?? ''}
                          onChange={(event) => {
                            setValues((prev) => ({
                              ...prev,
                              [task.id]: { qty: prev[task.id]?.qty ?? '0', reason: event.target.value },
                            }));
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardBody>
          </Card>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            {tc('actions.cancel')}
          </Button>
          <Button
            loading={confirm.isPending}
            disabled={open.length === 0}
            onClick={() => {
              confirm.mutate();
            }}
          >
            {t('paper.submit')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
